import React, { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { canEditMaintenance } from "../shared/MaintenanceGuard";
import { useNotice } from "../shared/useNotice";
import {
  listParties,
  createParty,
  listSettlementOrgs,
  Party,
  PartyInput,
  PartyType,
  GstStatus,
  OrgSummary,
} from "../../../api/settlement";

// ---- shared inline styles (maintenance aesthetic) ---------------------------
const card: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e0e0e0",
  borderRadius: 10,
  padding: 16,
  marginBottom: 16,
};
const primaryBtn: React.CSSProperties = {
  background: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  padding: "8px 14px",
  cursor: "pointer",
  fontWeight: 600,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};
const pillBase: React.CSSProperties = {
  display: "inline-block",
  padding: "2px 9px",
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 700,
};
const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  border: "1px solid #d0d0d0",
  borderRadius: 8,
  fontSize: 14,
  boxSizing: "border-box",
};
const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  marginBottom: 4,
  color: "#444",
};
const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "8px 10px",
  fontSize: 11.5,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  color: "#666",
  borderBottom: "1px solid #e0e0e0",
};
const tdStyle: React.CSSProperties = {
  padding: "8px 10px",
  fontSize: 13,
  borderBottom: "1px solid #f0f0f0",
  verticalAlign: "middle",
};

const PARTY_TYPE_PILL: Record<PartyType, React.CSSProperties> = {
  platform: { background: "#e8f0fe", color: "#2563eb" }, // blue
  host: { background: "#e8f7ef", color: "#1a7f4b" }, // green
  consultant: { background: "#f0e9fe", color: "#7c3aed" }, // violet
  custom: { background: "#f2f2f2", color: "#555" }, // grey
};

const PARTY_TYPE_OPTIONS: PartyType[] = ["platform", "host", "consultant", "custom"];
const GST_STATUS_OPTIONS: GstStatus[] = ["regular", "composition", "unregistered", "none"];

const GST_LABEL: Record<GstStatus, string> = {
  regular: "Regular",
  composition: "Composition",
  unregistered: "Unregistered",
  none: "None",
};

const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const emptyForm: PartyInput = {
  name: "",
  party_type: "host",
  gst_registration_status: "regular",
  gstin: "",
  default_sac: "",
  org_id: null,
};

const SettlementParties: React.FC = () => {
  const [parties, setParties] = useState<Party[]>([]);
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useNotice("error", "");
  const [message, setMessage] = useNotice("success", "");

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<PartyInput>(emptyForm);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const canEdit = canEditMaintenance();

  const fetchParties = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listParties();
      setParties(data);
    } catch (err) {
      console.error("Failed to fetch parties:", err);
      setError("Failed to load parties");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchParties();
    listSettlementOrgs().then(setOrgs).catch(() => undefined);
  }, [fetchParties]);

  const resetForm = () => {
    setForm(emptyForm);
    setFormError("");
  };

  const handleToggleForm = () => {
    if (showForm) {
      setShowForm(false);
      resetForm();
    } else {
      resetForm();
      setMessage("");
      setShowForm(true);
    }
  };

  const handleSave = async () => {
    setFormError("");
    if (!form.name.trim()) {
      setFormError("Party name is required");
      return;
    }
    setSaving(true);
    try {
      const body: PartyInput = {
        name: form.name.trim(),
        party_type: form.party_type,
        gst_registration_status: form.gst_registration_status,
        gstin: form.gstin?.trim() ? form.gstin.trim() : null,
        default_sac: form.default_sac?.trim() ? form.default_sac.trim() : null,
        org_id: form.org_id ?? null,
      };
      const created = await createParty(body);
      setShowForm(false);
      resetForm();
      setMessage(`Party "${created.name}" added`);
      await fetchParties();
    } catch (err: any) {
      console.error("Failed to create party:", err);
      setFormError(err?.response?.data?.detail || "Failed to save party");
    } finally {
      setSaving(false);
    }
  };

  return (
    <MaintenanceLayout title="Settlement · Parties">
      {/* Intro / add button */}
      <div style={card}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1, minWidth: 240 }}>
            <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>
              Parties directory
            </h3>
            <p style={{ margin: 0, fontSize: 13, color: "#777", lineHeight: 1.5 }}>
              Every entity that pays or receives money, reusable across profiles. A
              party&apos;s GST registration status decides how its share is invoiced (and
              whether reverse-charge applies). Custom entities &mdash; e.g. a consultant
              &mdash; are added here once and reused.
            </p>
          </div>
          {canEdit && (
            <button style={primaryBtn} onClick={handleToggleForm}>
              <Plus size={16} /> Add party / custom entity
            </button>
          )}
        </div>
      </div>

      {message && (
        <div
          style={{
            ...card,
            background: "#e8f7ef",
            border: "1px solid #b6e3c9",
            color: "#1a7f4b",
            fontWeight: 600,
          }}
        >
          {message}
        </div>
      )}

      {/* Inline add form */}
      {canEdit && showForm && (
        <div style={card}>
          <h4 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>
            Add party / custom entity
          </h4>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 12,
            }}
          >
            <div>
              <label style={labelStyle}>Name</label>
              <input
                style={inputStyle}
                type="text"
                value={form.name}
                placeholder="e.g. Apex Consulting"
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <label style={labelStyle}>Type</label>
              <select
                style={inputStyle}
                value={form.party_type}
                onChange={(e) =>
                  setForm({ ...form, party_type: e.target.value as PartyType })
                }
              >
                {PARTY_TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {titleCase(t)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Scope</label>
              <select
                style={inputStyle}
                value={form.org_id == null ? "" : String(form.org_id)}
                onChange={(e) =>
                  setForm({ ...form, org_id: e.target.value === "" ? null : Number(e.target.value) })
                }
              >
                <option value="">Global (shared across orgs)</option>
                {orgs.map((o) => (
                  <option key={o.org_id} value={o.org_id}>
                    {o.org_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>GST registration</label>
              <select
                style={inputStyle}
                value={form.gst_registration_status}
                onChange={(e) =>
                  setForm({
                    ...form,
                    gst_registration_status: e.target.value as GstStatus,
                  })
                }
              >
                {GST_STATUS_OPTIONS.map((g) => (
                  <option key={g} value={g}>
                    {GST_LABEL[g]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>
                GSTIN <span style={{ color: "#aaa", fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                style={inputStyle}
                type="text"
                value={form.gstin ?? ""}
                placeholder="29AABCC…1Z5"
                onChange={(e) => setForm({ ...form, gstin: e.target.value })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                Default SAC{" "}
                <span style={{ color: "#aaa", fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                style={inputStyle}
                type="text"
                value={form.default_sac ?? ""}
                placeholder="998714"
                onChange={(e) => setForm({ ...form, default_sac: e.target.value })}
              />
            </div>
          </div>

          {formError && (
            <p style={{ color: "#c62828", fontSize: 13, margin: "12px 0 0" }}>
              {formError}
            </p>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button
              style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save party"}
            </button>
            <button
              style={{
                background: "#fff",
                color: "#555",
                border: "1px solid #d0d0d0",
                borderRadius: 8,
                padding: "8px 14px",
                cursor: "pointer",
                fontWeight: 600,
              }}
              onClick={handleToggleForm}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Parties table */}
      <div style={card}>
        {loading ? (
          <p style={{ color: "#777", fontSize: 14 }}>Loading…</p>
        ) : error ? (
          <p style={{ color: "#c62828", fontSize: 14 }}>{error}</p>
        ) : parties.length === 0 ? (
          <p style={{ color: "#999", fontSize: 14 }}>No parties yet.</p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle}>Party</th>
                <th style={thStyle}>Scope</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>GST registration</th>
                <th style={thStyle}>GSTIN</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Used in</th>
              </tr>
            </thead>
            <tbody>
              {parties.map((p) => (
                <tr key={p.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{p.name}</td>
                  <td style={tdStyle}>
                    {p.org_id == null ? (
                      <span style={{ ...pillBase, background: "#eef2fb", color: "#2563eb" }}>Global</span>
                    ) : (
                      <span style={{ ...pillBase, background: "#e8f7ef", color: "#1a7f4b" }}>
                        {p.org_name || `Org #${p.org_id}`}
                      </span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <span style={{ ...pillBase, ...PARTY_TYPE_PILL[p.party_type] }}>
                      {titleCase(p.party_type)}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {p.gst_registration_status === "unregistered" ? (
                      <span
                        style={{
                          ...pillBase,
                          background: "#fff7e6",
                          color: "#b9770e",
                        }}
                      >
                        Unregistered &rarr; RCM
                      </span>
                    ) : (
                      <span style={{ color: "#444" }}>
                        {GST_LABEL[p.gst_registration_status]}
                      </span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, color: "#777" }}>{p.gstin || "—"}</td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>{p.used_in}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </MaintenanceLayout>
  );
};

export default SettlementParties;
